import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  CircularProgress,
  Typography,
  Select,
  Checkbox,
  MenuItem,
  ListItemText,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import axios from "axios";
import StyledButton from "../../../../common/models/Button";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import { appUrl } from "../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
    width: "100%",
    boxSizing: "border-box",
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    minWidth: 700,
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  iconBtn: {
    padding: "4px",
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 8,
    flexShrink: 0,
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 0",
    textTransform: "none",
    minWidth: 140,
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "8px 0",
    textTransform: "none",
    minWidth: 140,
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const ProductTypeTab = (props) => {
  const { featuresAccess, sponsorId } = props;
  const classes = useStyles();

  const [productTypes, setProductTypes] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [deletingStates, setDeletingStates] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [rowErrors, setRowErrors] = useState({});

  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const dispatch = (action) => {
    if (action.type === "OPEN_BACKDROP") {
    } else if (action.type === "DESTROY_BACKDROP") {
    }
  };

  const fetchProductOptions = async () => {
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchMaterialTypes/false`;
      const response = await axios.get(url);
      // materialTypeList is at root level of response (not nested under data)
      const list = response?.data?.materialTypeList;
      if (Array.isArray(list)) {
        const options = list
          .filter((item) => item.activated === true)
          .map((item) => ({
            id: item.materialTypeID,
            name: item.materialTypeName,
          }));
        setProductOptions(options);
      } else {
        console.error("Failed to fetch material type options.");
      }
    } catch (error) {
      console.error("Error fetching material type options:", error);
    }
  };

  const fetchProductTypes = async () => {
    setError(null);
    setLoading(true);
    dispatch({ type: "OPEN_BACKDROP" });
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchProductTypes/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;
      if (data?.status === true || data?.status === "true") {
        const list = data.productTypeList || [];
        const formatted = list.map((item, index) => ({
          id: item.productTypeID || `ID-${index + 1}`,
          name: item.productTypeName || "",
          type: item.productType,
          selected: !!item.activated,
          isNew: false,
          associatedMaterialTypes: (item?.associatedMaterialTypesList || []).map(
            (product) => ({
              id: product.materialTypeID,
              name: product.materialTypeName,
              productDomEqualsMaterialDom: product.productDomEqualsMaterialDom ?? false,
              // isEditing is NOT set for backend-fetched materials → they render as read-only text
            })
          ),
          lastUpdatedBy: item.lastUpdatedBy || "",
        }));
        setProductTypes(formatted);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      dispatch({ type: "DESTROY_BACKDROP" });
    }
  };

  useEffect(() => {
    fetchProductTypes();
  }, []);

  const saveNewProductType = async (productType) => {
    try {
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        productTypeList: [
          {
            productTypeName: productType.name,
            productType: "New",
            activated: productType.selected,
            associatedMaterialTypesList: productType?.products?.map((product) => ({
              materialTypeName: product?.name,
              materialTypeID: product?.id,
              productDomEqualsMaterialDom: product?.productDomEqualsMaterialDom ?? false,
            })),
          },
        ],
      };
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addProductTypes`;
      const response = await axios.post(url, payload);
      props.setProductType((prev) => !prev);
      if (response.data?.status === true || response.data?.status === "true") {
        return true;
      } else {
        console.error("Failed to save new product type.");
        return false;
      }
    } catch (error) {
      console.error("Error saving new product type:", error);
      return false;
    }
  };

  const handleSaveNewProductType = async (index) => {
    const productType = productTypes[index];
    if (savingStates[index]) return;
    const errors = {};
    if (!productType.name?.trim()) {
      errors.name = "Product Type is required.";
    }
    const hasEmptyMaterial =
      !productType.products?.length || productType.products.some((p) => p.id === null);
    if (hasEmptyMaterial) {
      errors.materials = "Please select a material type for all rows or remove the empty ones.";
    }
    if (Object.keys(errors).length > 0) {
      setRowErrors((prev) => ({ ...prev, [index]: errors }));
      return;
    }
    setRowErrors((prev) => ({ ...prev, [index]: {} }));
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    const success = await saveNewProductType(productType);
    if (success) {
      await fetchProductTypes();
    } else {
      console.error("Failed to save new product type.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  //     // Prepare the payload
  //     const payload = {
  //       sponsorId: 2,
  //       ecosystemId: localStorage.getItem("ecosystemId"),
  //       productTypeList: isAnyProductTypeSelected.map(
  //         ({ id, name, selected }) => ({
  //           productTypeID: id,
  //           productTypeName: name,
  //           activated: selected,
  //           markForDelete: false,
  //         })
  //       ),
  //     };

  //     console.log(payload, "Payload for updating Product Types"); // Debugging

  //     // Call the API
  //     const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateProductTypes`;
  //     const response = await axios.post(url, payload);

  //     // Handle API response
  //     if (response.data?.status === true || response.data?.status === "true") {
  //       console.log("Product Types updated successfully.");
  //       await fetchProductTypes(); // Refresh the product types list
  //       setErrorMessage(""); // Clear any previous error messages
  //       props.handleNext(); // Proceed to the next tab
  //     } else {
  //       console.error("Failed to update Product Types.");
  //       setErrorMessage("Failed to update Product Types. Please try again.");
  //     }
  //   } catch (error) {
  //     console.error("Error updating Product Types:", error);
  //     setErrorMessage(
  //       "An error occurred while updating Product Types. Please try again."
  //     );
  //   }
  // };
  const handleSaveChanges = async () => {
    try {
      const isAnyProductTypeSelected = productTypes.filter(
        (productType) => productType.isUpdated && !productType.isNew
      );
      if (!isAnyProductTypeSelected.length) return;

      if (productTypes.every((productType) => !productType.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }

      const invalidRows = isAnyProductTypeSelected.filter(
        (pt) => !pt.associatedMaterialTypes?.some((m) => m.id !== null)
      );
      if (invalidRows.length > 0) {
        setErrorMessage(
          `Associated Material Types cannot be empty. Please add at least one material type for: ${invalidRows.map((pt) => pt.name).join(", ")}.`
        );
        return;
      }

      const payload = {
        sponsorId: sponsorId,
        ecosystemId:  Number(localStorage.getItem("ecosystemId")),
        productTypeList: isAnyProductTypeSelected.map(
          ({ id, name, selected, associatedMaterialTypes }) => ({
            productTypeID: id,
            productTypeName: name,
            activated: selected,
            markForDelete: false,
            associatedMaterialTypesList:
              associatedMaterialTypes?.map((material) => ({
                materialTypeName: material?.name,
                materialTypeID: material?.id,
                productDomEqualsMaterialDom: material?.productDomEqualsMaterialDom ?? false,
              })) || [],
          })
        ),
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateProductTypes`;
      const response = await axios.post(url, payload);
      if (response.data?.status === true || response.data?.status === "true") {
        await fetchProductTypes();
        setErrorMessage("");
      } else {
        console.error("Failed to update Product Types.");
        setErrorMessage("Failed to update Product Types. Please try again.");
      }
    } catch (error) {
      console.error("Error updating Product Types:", error);
      setErrorMessage("An error occurred while updating Product Types. Please try again.");
    }
  };

  const deleteProductType = async (productType) => {
    setDeletingStates((prev) => ({ ...prev, [productType.id]: true }));
    try {
      if (productType.isNew) {
        setProductTypes((prev) => prev.filter((item) => item.id !== productType.id));
      } else {
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateProductTypes`;
        const payload = {
          sponsorId: sponsorId,
          ecosystemId: Number(localStorage.getItem("ecosystemId")),
          productTypeList: [
            {
              productTypeID: productType.id,
              productTypeName: productType.name,
              activated: productType.selected,
              markForDelete: true,
              associatedMaterialTypesList:
                productType?.associatedMaterialTypes?.map((product) => ({
                  materialTypeName: product?.name,
                  materialTypeID: product?.id,
                  productDomEqualsMaterialDom: product?.productDomEqualsMaterialDom ?? false,
                })),
            },
          ],
        };
        const response = await axios.post(url, payload);
        if (response.data?.status === true || response.data?.status === "true") {
          await fetchProductTypes();
          props.setProductType((prev) => !prev);
        } else {
          console.error("Failed to delete Product Type.");
         setErrorMessage("Failed to update Product Types. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error deleting Product Type:", error);
    } finally {
      setDeletingStates((prev) => ({ ...prev, [productType.id]: false }));
    }
  };

  const handleResetChanges = () => {
    if (isUpdated) {
      fetchProductTypes();
      setIsUpdated(false);
      setErrorMessage("");
    }
  };

  const handleToggle = (index) => {
    const updated = [...productTypes];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setProductTypes(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...productTypes];
    updated[index].name = value;
    setProductTypes(updated);
    if (rowErrors[index]?.name) {
      setRowErrors((prev) => ({ ...prev, [index]: { ...prev[index], name: "" } }));
    }
  };

  const handleAddProductType = () => {
    fetchProductOptions();
    setProductTypes([
      ...productTypes,
      {
        name: "",
        selected: false,
        isNew: true,
        // Start with one empty editable material row so selects appear immediately
        products: [{ id: null, name: "", productDomEqualsMaterialDom: false, isEditing: true }],
      },
    ]);
  };

  const handleCancel = (index) => {
    setProductTypes((prev) => prev.filter((_, i) => i !== index));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  // BCM-322: Add a new material type sub-row — flagged isEditing so controls stay active
  const handleAddMaterialType = (productTypeIndex) => {
    setProductTypes((prev) =>
      prev.map((row, i) => {
        if (i !== productTypeIndex) return row;
        const key = row.isNew ? "products" : "associatedMaterialTypes";
        const currentList = row[key] || [];
        return {
          ...row,
          [key]: [
            ...currentList,
            { id: null, name: "", productDomEqualsMaterialDom: false, isEditing: true },
          ],
          isUpdated: true,
        };
      })
    );
    setIsUpdated(true);
  };

  // BCM-322: Remove a material type sub-row
  const handleRemoveMaterialType = (productTypeIndex, materialIndex) => {
    setProductTypes((prev) =>
      prev.map((row, i) => {
        if (i !== productTypeIndex) return row;
        const key = row.isNew ? "products" : "associatedMaterialTypes";
        const currentList = row[key] || [];
        return {
          ...row,
          [key]: currentList.filter((_, j) => j !== materialIndex),
          isUpdated: true,
        };
      })
    );
    setIsUpdated(true);
  };

  // BCM-322: Change the DoM flag — preserves isEditing so DoM remains editable
  const handleDomFlagChange = (productTypeIndex, materialIndex, value) => {
    setProductTypes((prev) =>
      prev.map((row, i) => {
        if (i !== productTypeIndex) return row;
        const key = row.isNew ? "products" : "associatedMaterialTypes";
        const currentList = row[key] || [];
        let newList;
        if (currentList.length === 0 && materialIndex === 0) {
          // Virtual empty row — auto-create entry in state
          newList = [{ id: null, name: "", productDomEqualsMaterialDom: value, isEditing: true }];
        } else {
          newList = currentList.map((mat, j) =>
            j === materialIndex ? { ...mat, productDomEqualsMaterialDom: value } : mat
          );
        }
        return { ...row, [key]: newList, isUpdated: true };
      })
    );
    setIsUpdated(true);
  };

  // BCM-322: Select a material type — preserves isEditing so DoM stays editable after selection
  const handleMaterialTypeSelect = (productTypeIndex, materialIndex, selectedOption) => {
    setProductTypes((prev) =>
      prev.map((row, i) => {
        if (i !== productTypeIndex) return row;
        const key = row.isNew ? "products" : "associatedMaterialTypes";
        const currentList = row[key] || [];
        let newList;
        if (currentList.length === 0 && materialIndex === 0) {
          // Virtual empty row — auto-create entry in state
          newList = [{
            id: selectedOption.id,
            name: selectedOption.name,
            productDomEqualsMaterialDom: false,
            isEditing: true,
          }];
        } else {
          newList = currentList.map((mat, j) =>
            j === materialIndex
              ? { ...mat, id: selectedOption.id, name: selectedOption.name }
              // isEditing is spread-preserved → DoM dropdown stays active
              : mat
          );
        }
        return { ...row, [key]: newList, isUpdated: true };
      })
    );
    setIsUpdated(true);
    if (rowErrors[productTypeIndex]?.materials) {
      setRowErrors((prev) => ({
        ...prev,
        [productTypeIndex]: { ...prev[productTypeIndex], materials: "" },
      }));
    }
  };

  return (
    <div className={classes.container}>
      {/* Table + sidebar buttons */}
      <div style={{ display: "flex", width: "100%", gap: "24px", alignItems: "flex-start" }}>
        {/* Scrollable table wrapper */}
        <div className={classes.tableWrapper}>
          <Table className={`${classes.table} default-table-styles`}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Selection</TableCell>
                <TableCell className={classes.headerCell}>Product Type</TableCell>
                <TableCell className={classes.headerCell}>Product Type ID</TableCell>
                <TableCell className={classes.headerCell}>
                  Associated Material Types
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Product DoM = Material DoM
                </TableCell>
                <TableCell className={classes.headerCell}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60px",
                        backgroundColor: "#ffecec",
                        color: "#b00020",
                        border: "1px solid #ff9999",
                      }}
                    >
                      <Typography>{error}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                productTypes.map((item, index) => {
                  const isDefault = item.type?.toLowerCase() === "default";
                  // For new rows use item.products; for existing rows use associatedMaterialTypes
                  const materials = item.isNew
                    ? item.products || []
                    : item.associatedMaterialTypes || [];
                  // When materials list is empty and row is editable, show a virtual empty row
                  // so the selects appear immediately without needing to click ⊕ first
                  const emptyEditableMaterial = { id: null, name: "", productDomEqualsMaterialDom: false, isEditing: true };
                  const effectiveMaterials =
                    materials.length === 0 && canEdit && !isDefault
                      ? [emptyEditableMaterial]
                      : materials;

                  return (
                    <TableRow key={item.id || index}>
                      {/* Selection */}
                      <TableCell className={classes.bodyCell}>
                        <Switch
                          checked={item.selected}
                          onChange={() => handleToggle(index)}
                          classes={{ switchBase: classes.switchBase }}
                          // disabled={!canEdit || isDefault}
                        />
                      </TableCell>

                      {/* Product Type name / text field */}
                      <TableCell className={classes.bodyCell}>
                        {item.isNew ? (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                            <TextField
                              placeholder="Type Text Here"
                              value={item.name}
                              onChange={(e) => handleNameChange(index, e.target.value)}
                              onBlur={(e) => {
                                const { value } = e.target;
                                if (typeof value === "string" && value.trim() !== value) {
                                  handleNameChange(index, value.trim());
                                }
                              }}
                              className={classes.textField}
                              variant="outlined"
                              error={!!rowErrors[index]?.name}
                              inputProps={{
                                onBeforeInput: (e) => {
                                  if (!/^[a-zA-Z0-9\s()/\-.]+$/.test(e.data)) {
                                    e.preventDefault();
                                  }
                                },
                              }}
                            />
                            {rowErrors[index]?.name && (
                              <Typography style={{ color: "red", fontSize: 11, marginTop: 2 }}>
                                {rowErrors[index].name}
                              </Typography>
                            )}
                          </div>
                            <div className={classes.inlineActions}>
                              {savingStates[index] ? (
                                <CircularProgress size={20} style={{ margin: "4px" }} />
                              ) : (
                                <>
                                  <IconButton
                                    onClick={() => handleCancel(index)}
                                    className={classes.iconBtn}
                                    title="Cancel"
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => handleSaveNewProductType(index)}
                                    className={classes.iconBtn}
                                    title="Save"
                                    disabled={
                                      !productTypes[index].name?.trim() ||
                                      productTypes.some(
                                        (p, i) =>
                                          i !== index &&
                                          p.name?.trim().toLowerCase() ===
                                            productTypes[index].name?.trim().toLowerCase()
                                      ) ||
                                      !(
                                        productTypes[index].products?.length &&
                                        productTypes[index].products.every((p) => p.id !== null)
                                      )
                                    }
                                  >
                                    <SaveIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          item.name
                        )}
                      </TableCell>

                      {/* Product Type ID */}
                      <TableCell className={classes.bodyCell}>{item.id}</TableCell>

                      {/* Associated Material Types */}
                      <TableCell
                        className={classes.bodyCell}
                        style={{ verticalAlign: "top" }}
                      >
                        <div>
                          {effectiveMaterials.map((material, matIdx) => {
                            const isLastMaterial = matIdx === effectiveMaterials.length - 1;
                            return (
                              <div
                                key={matIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: 4,
                                  minHeight: 36,
                                }}
                              >
                                {/* isEditing → dropdown; otherwise → plain text */}
                                {material.isEditing ? (
                                  <Select
                                    displayEmpty
                                    value={material.id || ""}
                                    onChange={(e) => {
                                      const selected = productOptions.find(
                                        (opt) => opt.id === e.target.value
                                      );
                                      if (selected) {
                                        handleMaterialTypeSelect(index, matIdx, selected);
                                      }
                                    }}
                                    onOpen={fetchProductOptions}
                                    renderValue={(val) =>
                                      val
                                        ? productOptions.find((opt) => opt.id === val)
                                            ?.name || "Select"
                                        : "Select from dropdown"
                                    }
                                    style={{ width: 180 }}
                                    size="small"
                                    variant="outlined"
                                    disabled={!canEdit}
                                  >
                                    {productOptions
                                      .filter(
                                        (opt) =>
                                          !effectiveMaterials.some(
                                            (m, mi) => mi !== matIdx && m.id === opt.id
                                          )
                                      )
                                      .map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                          {option.name}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                ) : (
                                  <Typography style={{ minWidth: 150 }}>
                                    {material.name}
                                  </Typography>
                                )}

                                {/* ⊕ Add button — inline on the last material row */}
                                {canEdit && !isDefault && isLastMaterial && (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      fetchProductOptions();
                                      handleAddMaterialType(index);
                                    }}
                                    title="Add material type"
                                    style={{ marginLeft: 2 }}
                                  >
                                    <AddCircleOutlineIcon
                                      fontSize="small"
                                      style={{ color: "#007bff" }}
                                    />
                                  </IconButton>
                                )}

                                {/* Delete — only for isEditing rows that exist in real state */}
                                {canEdit && !isDefault && material.isEditing && materials.length > 0 && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleRemoveMaterialType(index, matIdx)
                                    }
                                    style={{ marginLeft: 2 }}
                                  >
                                    <DeleteIcon
                                      fontSize="small"
                                      className={classes.deleteIcon}
                                    />
                                  </IconButton>
                                )}
                              </div>
                            );
                          })}
                          {item.isNew && rowErrors[index]?.materials && (
                            <Typography style={{ color: "red", fontSize: 11, marginTop: 2 }}>
                              {rowErrors[index].materials}
                            </Typography>
                          )}
                        </div>
                      </TableCell>

                      {/* Product DoM = Material DoM */}
                      <TableCell
                        className={classes.bodyCell}
                        style={{ verticalAlign: "top" }}
                      >
                        <div>
                          {effectiveMaterials.map((material, matIdx) => (
                            <div
                              key={matIdx}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 4,
                                minHeight: 36,
                              }}
                            >
                              {/* isEditing → editable Select; otherwise → plain text */}
                              {material.isEditing ? (
                                <Select
                                  value={
                                    material.productDomEqualsMaterialDom ? "Yes" : "No"
                                  }
                                  onChange={(e) =>
                                    handleDomFlagChange(
                                      index,
                                      matIdx,
                                      e.target.value === "Yes"
                                    )
                                  }
                                  style={{ width: 100 }}
                                  size="small"
                                  variant="outlined"
                                  disabled={!canEdit}
                                >
                                  <MenuItem value="Yes">Yes</MenuItem>
                                  <MenuItem value="No">No</MenuItem>
                                </Select>
                              ) : (
                                <Typography style={{ minWidth: 40 }}>
                                  {material.productDomEqualsMaterialDom ? "Yes" : "No"}
                                </Typography>
                              )}
                            </div>
                          ))}
                        </div>
                      </TableCell>

                      {/* Action */}
                      <TableCell
                        className={classes.bodyCell}
                        style={{ verticalAlign: "top" }}
                      >
                        {item.isNew ? (
                          <IconButton
                            aria-label="discard"
                            onClick={() => handleCancel(index)}
                            className={classes.iconBtn}
                          >
                            <DeleteIcon className={classes.deleteIcon} />
                          </IconButton>
                        ) : (
                          canEdit &&
                          !isDefault &&
                          item.type === "New" &&
                          item.lastUpdatedBy !== "support@schrocken.com" && (
                            deletingStates[item.id] ? (
                              <CircularProgress size={20} style={{ margin: "4px" }} />
                            ) : (
                              <IconButton
                                aria-label="delete"
                                onClick={() => deleteProductType(item)}
                              >
                                <DeleteIcon className={classes.deleteIcon} />
                              </IconButton>
                            )
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Save / Reset buttons — fixed width beside the table */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "12px",
            paddingTop: "8px",
            flexShrink: 0,
          }}
        >
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit}
            style={{
              opacity: !isUpdated || !canEdit ? 0.7 : 1,
              color: "#fff",
            }}
          >
            Save Selection
          </Button>
          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
            disabled={!isUpdated || !canEdit}
            style={{
              opacity: !isUpdated || !canEdit ? 0.8 : 1,
            }}
          >
            Reset Selection
          </Button>
        </div>
      </div>

      {errorMessage && (
        <div style={{ color: "red", marginTop: 8 }} className="error-message">
          {errorMessage}
        </div>
      )}

      {canEdit && (
        <Button
          variant="outlined"
          onClick={handleAddProductType}
          className={classes.addButton}
        >
          <span className={classes.addIconCircle}>+</span>
          Add New Row
        </Button>
      )}
    </div>
  );
};

export default ProductTypeTab;
